// client/src/admin/login/AdminForgotPassword.js
import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
  alpha,
  useTheme,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import KeyIcon from "@mui/icons-material/Key";
import ShieldIcon from "@mui/icons-material/Shield";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { GlobalContext } from "../../context/GlobalContext";
import { useHistory } from "react-router-dom";

const STEPS = ["Enter Email", "Verify OTP", "New Password"];

const AdminForgotPassword = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const history = useHistory();

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSendOTP = async () => {
    if (!email) return setError("Please enter your email");
    setError("");
    setLoading(true);
    const res = await hitAxios({
      path: "/api/admin/forgot-password",
      post: true,
      obj: { email },
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setStep(1);
      setSuccess("Reset code sent if that admin email exists.");
    } else {
      setError(res?.data?.msg || "Something went wrong");
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return setError("Enter the 6-digit OTP");
    setError("");
    setLoading(true);
    // We verify inline on reset — just move to step 2
    setLoading(false);
    setStep(2);
  };

  const handleResetPassword = async () => {
    if (!newPassword) return setError("Please enter a new password");
    if (newPassword !== confirmPassword)
      return setError("Passwords do not match");
    setError("");
    setLoading(true);
    const res = await hitAxios({
      path: "/api/admin/reset-password",
      post: true,
      obj: { email, otp, new_password: newPassword },
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setSuccess("Password reset! Redirecting to login...");
      setTimeout(() => history.push("/admin/login"), 2000);
    } else {
      setError(res?.data?.msg || "Invalid or expired OTP");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: isDark
          ? "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)"
          : "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 50%, #F5F3FF 100%)",
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        {/* Security badge */}
        <Stack direction="row" justifyContent="center" mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: 10,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <ShieldIcon sx={{ fontSize: 14, color: "warning.main" }} />
            <Typography
              variant="caption"
              color="warning.main"
              fontWeight={700}
              sx={{ fontSize: "0.7rem" }}
            >
              Admin Password Recovery
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.8)
              : "background.paper",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.08)}`,
            boxShadow: isDark
              ? `0 24px 80px ${alpha("#000", 0.5)}`
              : `0 24px 60px ${alpha("#000", 0.1)}`,
          }}
        >
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                margin: "0 auto 16px",
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${alpha(theme.palette.warning.main, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 12px 32px ${alpha(theme.palette.warning.main, 0.4)}`,
              }}
            >
              <KeyIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ letterSpacing: "-0.02em" }}
            >
              Reset Admin Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {step === 0 && "Enter your admin email address"}
              {step === 1 && "Enter the 6-digit code from your email"}
              {step === 2 && "Set your new admin password"}
            </Typography>
          </Box>

          <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{ "& .MuiStepLabel-label": { fontSize: "0.7rem" } }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          <Stack spacing={2}>
            {step === 0 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Admin Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOTP}
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: theme.palette.warning.main,
                    "&:hover": { bgcolor: theme.palette.warning.dark },
                  }}
                >
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </>
            )}

            {step === 1 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyOTP()}
                  inputProps={{
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      letterSpacing: "0.5rem",
                    },
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: theme.palette.warning.main,
                    "&:hover": { bgcolor: theme.palette.warning.dark },
                  }}
                >
                  Continue
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => {
                    setStep(0);
                    setOtp("");
                    setError("");
                  }}
                  sx={{ borderRadius: 2, color: "text.secondary" }}
                >
                  ← Back
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 18 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Confirm New Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                  error={!!confirmPassword && newPassword !== confirmPassword}
                  helperText={
                    !!confirmPassword && newPassword !== confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleResetPassword}
                  disabled={
                    loading || !newPassword || newPassword !== confirmPassword
                  }
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: theme.palette.warning.main,
                    "&:hover": { bgcolor: theme.palette.warning.dark },
                  }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </>
            )}

            <Button
              fullWidth
              variant="text"
              size="small"
              onClick={() => history.push("/admin/login")}
              sx={{ borderRadius: 2, color: "text.secondary" }}
            >
              Back to Admin Login
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminForgotPassword;
